import {useEffect, useContext, useState} from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/EditOutlined';
import {Auth} from '@taruvi/sdk';
import {taruviClient} from './taruviClient.ts';
import {useIsAuthenticated} from '@refinedev/core';
import Navkit from '@taruvi/navkit';
import {GridContainer} from './components/GridContainer.tsx';
import SpaceProvider, {SpaceContext} from "./SpaceProvider.tsx";
import EditDrawer from "./components/EditDrawer.tsx";
import ApplicationsView from "./components/ApplicationsView.tsx";

type View = 'dashboard' | 'applications';

const tabs: { id: View; label: string; icon: string }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { id: 'applications', label: 'Applications', icon: 'apps' },
];

const AppContent = () => {
  const [, , , , isEditing, enterEditMode, cancelEditMode, me] = useContext(SpaceContext)!;
  const [view, setView] = useState<View>('dashboard');

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap" rel="stylesheet" />
      <div style={{position: 'sticky', top: 0, zIndex: 20}}>
        <Navkit client={taruviClient} navbarColor="#4285f4" iconColor="#ffffff" />
      </div>
      <Box sx={{display: 'flex'}}>
        <main style={{flex: 1, minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', p: '24px 32px 0', position: 'sticky', top: 64, zIndex: 10, bgcolor: 'inherit'}}>
            <Box sx={{display: 'flex', gap: 0.5, bgcolor: '#ffffff', borderRadius: '28px', p: 0.5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)'}}>
              {tabs.map(({id, label, icon}) => {
                const isActive = view === id;
                return (
                  <Box
                    key={id}
                    onClick={() => setView(id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2.5,
                      py: 1.2,
                      borderRadius: '24px',
                      cursor: 'pointer',
                      color: isActive ? '#fff' : '#64748b',
                      bgcolor: isActive ? '#4285f4' : 'transparent',
                      boxShadow: isActive ? '0 4px 16px rgba(66,133,244,0.25)' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: isActive ? '#3b78e0' : 'rgba(66,133,244,0.06)',
                      },
                    }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: 20,
                        fontVariationSettings: isActive ? "'FILL' 1, 'wght' 500" : "'FILL' 0, 'wght' 400",
                      }}
                    >
                      {icon}
                    </span>
                    <Typography sx={{fontSize: 14, fontWeight: isActive ? 600 : 500}}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {view === 'dashboard' && (
            <>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '16px 32px 0'}}>
                <Typography sx={{fontFamily: '"Quicksand", sans-serif', fontWeight: 700, fontSize: '1.5rem'}}>
                  Welcome {me?.first_name}!
                </Typography>
                {!isEditing && (
                  <Tooltip title="Edit layout" arrow>
                    <IconButton size="small" onClick={enterEditMode}
                      sx={{color: '#fff', bgcolor: '#1E88F5', '&:hover': {bgcolor: '#1565C0'}, width: 36, height: 36}}>
                      <EditIcon sx={{fontSize: 20}} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <GridContainer />
            </>
          )}
          {view === 'applications' && <ApplicationsView />}
        </main>
        {isEditing && view === 'dashboard' && <EditDrawer isEditing={isEditing} cancelEditMode={cancelEditMode} />}
      </Box>
    </>
  );
};

const App = () => {
  const {data, isLoading} = useIsAuthenticated();

  useEffect(() => {
    if (!isLoading && !data?.authenticated) {
      const auth = new Auth(taruviClient);
      auth.login();
    }
  }, [isLoading, data]);

  if (isLoading || !data?.authenticated) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SpaceProvider>
      <AppContent />
    </SpaceProvider>
  );
};

export default App;
